/**
 * Pure presentation/visibility helpers for the navigation bars.
 *
 * BottomNav (mobile) and NavigationBar (desktop) previously each carried an
 * identical inline `colorSchemeProps` closure and `isAdmin` expression — a
 * straight DRY violation that also left this logic untested (both copies lived
 * inside components that need the Chakra colour-mode and stats contexts to
 * render). Extracted here once so both navs share it and it can be unit-tested.
 */

/**
 * Whether the signed-in user is an administrator.
 *
 * Admin affordances must be gated on BOTH an active sign-in and the admin role —
 * a stale role value with no session must never reveal admin controls.
 *
 * @param isSignedIn - Whether the user currently has a valid session.
 * @param roleName - The user's role name from the stats payload, if any.
 * @returns True only when signed in AND the role is exactly 'admin'.
 */
export const isAdminRole = (isSignedIn: boolean, roleName: string | undefined): boolean =>
  isSignedIn && roleName === 'admin'

export interface ViewModeColorSchemeOptions {
  /** The view mode this button represents (e.g. 'global', 'user', 'admin'). */
  matchingMode: string
  /** The currently active view mode. */
  viewMode: string
  /** The Chakra colour scheme variant for the button (e.g. 'green', 'purple'). */
  variant: string
  /** Whether the UI is in light mode (affects the shade ramp). */
  lightMode: boolean
}

/**
 * Build the Chakra style props for a view-mode toggle button.
 *
 * The active (matching) button is filled with the variant colour; inactive
 * buttons are transparent with the variant as text colour. Light mode uses a
 * lighter shade ramp than dark mode. Text colour is always white on the filled
 * state.
 *
 * @param options - The matching/active modes, the variant, and light-mode flag.
 * @returns Chakra style props (bg, color, _hover, variant) for the button.
 */
export const buildViewModeColorScheme = ({
  matchingMode,
  viewMode,
  variant,
  lightMode,
}: ViewModeColorSchemeOptions) => {
  const isMatchingMode = viewMode === matchingMode
  const bgColor = lightMode ? `${variant}.500` : `${variant}.600`
  const hoverBgColor = lightMode ? `${variant}.400` : `${variant}.700`
  const nonMatchingTextColor = lightMode ? `${variant}.600` : `${variant}.500`

  return {
    bg: isMatchingMode ? bgColor : 'transparent',
    color: isMatchingMode ? 'white' : nonMatchingTextColor,
    _hover: isMatchingMode
      ? {
          bg: hoverBgColor,
          color: 'white',
        }
      : {
          bg: 'transparent',
          color: bgColor,
        },
    variant,
  }
}
